import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GrowthSlot, GrowthSlotName } from 'src/features/Growth/growth-slot'
import {
  getLocalizedRecordData,
  type I18nRecordDataType,
} from 'src/i18n/localizedContent'
import { useBanners } from 'src/services/orpc/banner'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import { DEFAULT_LOCALE } from 'src/stores/intl'
import { cn } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import {
  BannerCarousel,
  BANNER_ASPECT_RATIO_DESKTOP,
  BANNER_ASPECT_RATIO_MOBILE,
  type BannerSlide,
} from './BannerCarousel'
import { isExpired } from './utils'

type LocalizedString = string | I18nRecordDataType<string>

interface BannerCarouselSettings {
  autoplayInterval?: number
  firstSlideInterval?: number
}

function useBannerConfig() {
  const { data, isLoading } = useBanners()
  const { data: settings } =
    usePublicDynamicConfig<BannerCarouselSettings>('carousel')
  return { data, settings, isLoading }
}

export function HomeBannerCarousel({
  className,
  style,
  containerClassName,
  containerStyle,
}: {
  className?: string
  style?: React.CSSProperties
  containerClassName?: string
  containerStyle?: React.CSSProperties
}) {
  const { data, settings, isLoading } = useBannerConfig()
  const isMobile = useIsMobile()
  const { i18n } = useTranslation()
  const lang = i18n.resolvedLanguage ?? DEFAULT_LOCALE

  const slides = useMemo<BannerSlide[]>(() => {
    const resolve = (
      value: LocalizedString | null | undefined,
    ): string | undefined => {
      if (value == null) return undefined
      if (typeof value === 'string') return value
      const resolved = getLocalizedRecordData(lang, value)
      return typeof resolved === 'string' ? resolved : undefined
    }
    return (data ?? [])
      .filter(item => !isExpired(item.endTime ?? undefined))
      .flatMap(item => {
        const imageUrl = resolve(item.imageUrl)
        const targetUrl = resolve(item.link)
        if (!imageUrl || !targetUrl) return []
        return {
          id: item.label,
          imageUrl,
          mobileImageUrl: resolve(item.mobileImageUrl),
          targetUrl,
          alt: resolve(item.title),
          blank: item.blank,
        }
      })
  }, [data, lang])

  if (isLoading) {
    return (
      <GrowthSlot
        name={GrowthSlotName.HomeBanner}
        className={containerClassName}
        style={containerStyle}
      >
        <div
          className={cn(
            'bg-zinc-800/50 animate-pulse',
            isMobile ? 'rounded-xl' : 'rounded-2xl',
            className,
          )}
          // Picks the mobile ratio on narrow screens because the desktop
          // 5.6:1 collapses to a sliver there — banners that don't ship a
          // mobileImageUrl will reflow once BannerImage falls back to the
          // desktop image, but that's a config gap (mobile slides are
          // expected to provide a mobile-shaped image).
          // Spread caller style first so this aspectRatio always wins.
          style={{
            ...style,
            aspectRatio: isMobile
              ? BANNER_ASPECT_RATIO_MOBILE
              : BANNER_ASPECT_RATIO_DESKTOP,
          }}
          aria-hidden="true"
        />
      </GrowthSlot>
    )
  }

  if (slides.length === 0) return null

  return (
    <GrowthSlot
      name={GrowthSlotName.HomeBanner}
      className={containerClassName}
      style={containerStyle}
    >
      <BannerCarousel
        slides={slides}
        className={className}
        style={style}
        autoplayInterval={settings?.autoplayInterval}
        firstSlideInterval={settings?.firstSlideInterval}
        isMobile={isMobile ?? false}
      />
    </GrowthSlot>
  )
}
